import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, ChevronLeft, Trash2, Search, Loader2, FileText, User,
  Bold, Italic, Strikethrough, List, ListOrdered, Heading1, Heading2, Heading3, Maximize2, Minimize2, Check
} from 'lucide-react';
import { notesService } from './notesService';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import { useSecureAuth } from '../../../contexts/SecureAuthContext';
import './Notes.css';

const AUTOSAVE_MS = 800;

function timeAgo(date) {
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

/**
 * Strip from-paste HTML to plain text in the body field on paste, so the
 * user can't paste a styled fragment containing scripts/iframes/etc.
 * The editor's own toolbar produces the only formatting that ends up in
 * the body — keeps the stored HTML predictable.
 */
function stripPaste(event) {
  event.preventDefault();
  const text = (event.clipboardData || window.clipboardData).getData('text/plain');
  document.execCommand('insertText', false, text);
}

/**
 * Owner-only notes are low-risk for XSS (self-XSS at worst), but we
 * still belt-and-braces: strip script tags, event-handler attributes,
 * and javascript: URLs before persisting.
 */
function sanitizeHTML(html) {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '');
}

const Notes = ({ compact = false, onViewChange, onClose }) => {
  const { confirm } = useConfirm();
  const { user } = useSecureAuth();
  const writerName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.username ||
    user?.businessName ||
    'You';

  const [view, setView] = useState('list');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [savingState, setSavingState] = useState('idle'); // idle | saving | saved | error
  const [expanded, setExpanded] = useState(false);
  const autosaveRef = useRef(null);
  const bodyRef = useRef(null);

  // ── External signals ────────────────────────────────────────────────
  useEffect(() => {
    onViewChange?.({ view, expanded, count: notes.length });
  }, [view, expanded, notes.length, onViewChange]);

  // ── Data ────────────────────────────────────────────────────────────
  const refresh = useCallback(async (searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const data = await notesService.list(searchTerm);
      setNotes(data);
    } catch (err) {
      console.error('[Notes] list failed', err);
      setError('Could not load your notes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const t = setTimeout(() => { refresh(q); }, 250);
    return () => clearTimeout(t);
  }, [q, refresh]);

  // ── Editor lifecycle ────────────────────────────────────────────────
  const openEditor = useCallback((note) => {
    setSelected(note);
    setDirty(false);
    setSavingState('idle');
    setView('editor');
  }, []);

  const closeEditor = useCallback(() => {
    setView('list');
    setSelected(null);
    setExpanded(false);
    setDirty(false);
  }, []);

  // Sync the contentEditable's innerHTML when switching notes only —
  // not on every keystroke (that would clobber the caret).
  useEffect(() => {
    if (view !== 'editor') return;
    if (!bodyRef.current) return;
    if (!selected) return;
    if (bodyRef.current.innerHTML !== (selected.body || '')) {
      bodyRef.current.innerHTML = selected.body || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, view]);

  const handleNew = useCallback(async () => {
    try {
      const note = await notesService.create({ title: null, body: '' });
      setNotes((prev) => [note, ...prev]);
      openEditor(note);
    } catch (err) {
      console.error('[Notes] create failed', err);
      setError('Could not create note.');
    }
  }, [openEditor]);

  const flushSave = useCallback(async (target) => {
    if (!target) return;
    setSavingState('saving');
    try {
      const cleanBody = sanitizeHTML(target.body);
      const saved = await notesService.update(target.id, {
        title: target.title,
        body: cleanBody
      });
      setSelected((cur) => (cur && cur.id === saved.id
        ? { ...cur, title: saved.title, body: saved.body, updatedAt: saved.updatedAt }
        : cur));
      setNotes((prev) => prev.map((n) => (n.id === saved.id
        ? { ...n, title: saved.title, body: saved.body, updatedAt: saved.updatedAt }
        : n)));
      setDirty(false);
      setSavingState('saved');
      setTimeout(() => setSavingState((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (err) {
      console.error('[Notes] save failed', err);
      setSavingState('error');
    }
  }, []);

  const scheduleAutosave = useCallback((next) => {
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => { flushSave(next); }, AUTOSAVE_MS);
  }, [flushSave]);

  const updateField = (patch) => {
    const next = { ...selected, ...patch };
    setSelected(next);
    setDirty(true);
    scheduleAutosave(next);
  };

  const handleTitleChange = (value) => updateField({ title: value });

  const handleBodyInput = () => {
    // contentEditable: read the live innerHTML and stash it in state.
    // Don't write it back — that would reset the caret.
    if (!bodyRef.current) return;
    const next = { ...selected, body: bodyRef.current.innerHTML };
    setSelected(next);
    setDirty(true);
    scheduleAutosave(next);
  };

  const handleSaveClick = () => {
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    flushSave(selected);
  };

  // ── Toolbar (real formatting via execCommand) ───────────────────────
  const applyToolbar = (kind) => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    switch (kind) {
      case 'b': document.execCommand('bold'); break;
      case 'i': document.execCommand('italic'); break;
      case 's': document.execCommand('strikeThrough'); break;
      case 'ul': document.execCommand('insertUnorderedList'); break;
      case 'ol': document.execCommand('insertOrderedList'); break;
      case 'h1': document.execCommand('formatBlock', false, 'H1'); break;
      case 'h2': document.execCommand('formatBlock', false, 'H2'); break;
      case 'h3': document.execCommand('formatBlock', false, 'H3'); break;
      default: break;
    }
    handleBodyInput();
  };

  // ── Delete (clean UI confirm, not browser confirm) ──────────────────
  const confirmAndDelete = async (note, fromEditor = false) => {
    const ok = await confirm(
      `“${note.title?.trim() || 'Untitled'}” will be permanently removed.`,
      {
        title: 'Delete this note?',
        confirmText: 'Delete',
        cancelText: 'Keep',
        type: 'danger'
      }
    );
    if (!ok) return;
    try {
      await notesService.remove(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      if (fromEditor) closeEditor();
    } catch (err) {
      console.error('[Notes] delete failed', err);
      setError('Could not delete note.');
    }
  };

  // ── Render: list view ───────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div className={`rsn ${compact ? 'rsn--compact' : ''}`}>
        <div className="rsn__search">
          <Search size={14} aria-hidden="true" className="rsn__search-icon" />
          <input
            type="search"
            className="rsn__search-input"
            placeholder="Search notes"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {error && <div className="rsn__error">{error}</div>}

        {loading ? (
          <div className="rsn__loading"><Loader2 size={16} className="rsn__spin" aria-hidden="true" /> Loading…</div>
        ) : notes.length === 0 ? (
          <div className="rsn__empty">
            <span className="rsn__empty-icon" aria-hidden="true"><FileText size={20} /></span>
            <p className="rsn__empty-title">{q ? 'No notes match' : 'No notes yet'}</p>
            <p className="rsn__empty-sub">
              {q ? 'Try a different search.' : 'Capture a thought, a checklist, anything.'}
            </p>
          </div>
        ) : (
          <ul className="rsn__list" role="list">
            {notes.map((n) => (
              <li key={n.id}>
                <div
                  className="rsn__item"
                  onClick={() => openEditor(n)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditor(n); } }}
                >
                  <div className="rsn__item-top">
                    <div className="rsn__item-head">
                      <span className="rsn__item-title">{n.title?.trim() || 'Untitled'}</span>
                      <span className="rsn__item-time">{timeAgo(n.updatedAt)}</span>
                    </div>
                    <button
                      type="button"
                      className="rsn__item-delete"
                      onClick={(e) => { e.stopPropagation(); confirmAndDelete(n); }}
                      aria-label={`Delete ${n.title || 'note'}`}
                      title="Delete"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                  <div className="rsn__item-divider" aria-hidden="true" />
                  <div className="rsn__item-foot">
                    <span className="rsn__item-author">
                      <span className="rsn__item-avatar" aria-hidden="true"><User size={12} /></span>
                      <span>{writerName}</span>
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="rsn__fab"
          onClick={handleNew}
          aria-label="New note"
          title="New note"
        >
          <Plus size={18} aria-hidden="true" />
        </button>
      </div>
    );
  }

  // ── Render: editor view (workspace) ─────────────────────────────────
  if (!selected) return null;

  const saveLabel =
    savingState === 'saving' ? 'Saving…'
    : savingState === 'saved' ? 'Saved'
    : savingState === 'error' ? 'Retry save'
    : 'Save';

  return (
    <div className={`rsn rsn--editor ${expanded ? 'rsn--expanded' : ''}`}>
      <div className="rsn__editor-head">
        <button
          type="button"
          className="rsn__back"
          onClick={closeEditor}
          aria-label="Back to notes"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <div className="rsn__editor-meta">
          <span className="rsn__editor-heading">{selected.title?.trim() || 'Untitled'}</span>
          <span className="rsn__editor-subheading">
            {selected.title?.trim() ? `Updated ${timeAgo(selected.updatedAt)}` : 'New note'}
          </span>
        </div>
        <button
          type="button"
          className="rsn__delete"
          onClick={() => confirmAndDelete(selected, true)}
          aria-label="Delete note"
          title="Delete"
        >
          <Trash2 size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`rsn__save ${savingState === 'saved' ? 'is-saved' : ''}`}
          onClick={handleSaveClick}
          disabled={savingState === 'saving' || (!dirty && savingState !== 'error')}
        >
          {savingState === 'saving' && <Loader2 size={14} className="rsn__spin" aria-hidden="true" />}
          {savingState === 'saved' && <Check size={14} aria-hidden="true" />}
          <span>{saveLabel}</span>
        </button>
      </div>

      <div className="rsn__editor-body">
        <input
          className="rsn__title-input"
          type="text"
          placeholder="Title"
          value={selected.title || ''}
          onChange={(e) => handleTitleChange(e.target.value)}
          maxLength={200}
        />

        <div
          ref={bodyRef}
          className="rsn__body-input"
          contentEditable
          suppressContentEditableWarning
          onInput={handleBodyInput}
          onPaste={stripPaste}
          data-placeholder="Start writing…"
          spellCheck
        />
      </div>

      <div className="rsn__toolbar" role="toolbar" aria-label="Formatting">
        <div className="rsn__toolbar-group">
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('b'); }} aria-label="Bold" title="Bold">
            <Bold size={15} aria-hidden="true" />
          </button>
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('i'); }} aria-label="Italic" title="Italic">
            <Italic size={15} aria-hidden="true" />
          </button>
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('s'); }} aria-label="Strikethrough" title="Strikethrough">
            <Strikethrough size={15} aria-hidden="true" />
          </button>
        </div>

        <div className="rsn__toolbar-divider" aria-hidden="true" />

        <div className="rsn__toolbar-group">
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('ul'); }} aria-label="Bulleted list" title="Bulleted list">
            <List size={15} aria-hidden="true" />
          </button>
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('ol'); }} aria-label="Numbered list" title="Numbered list">
            <ListOrdered size={15} aria-hidden="true" />
          </button>
        </div>

        <div className="rsn__toolbar-divider" aria-hidden="true" />

        <div className="rsn__toolbar-group">
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('h1'); }} aria-label="Heading 1" title="Heading 1">
            <Heading1 size={15} aria-hidden="true" />
          </button>
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('h2'); }} aria-label="Heading 2" title="Heading 2">
            <Heading2 size={15} aria-hidden="true" />
          </button>
          <button type="button" className="rsn__tool" onMouseDown={(e) => { e.preventDefault(); applyToolbar('h3'); }} aria-label="Heading 3" title="Heading 3">
            <Heading3 size={15} aria-hidden="true" />
          </button>
        </div>

        <div className="rsn__toolbar-spacer" />

        <button
          type="button"
          className="rsn__tool rsn__tool--end"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Exit expanded view' : 'Expand'}
          title={expanded ? 'Exit expanded' : 'Expand'}
        >
          {expanded ? <Minimize2 size={15} aria-hidden="true" /> : <Maximize2 size={15} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
};

export default Notes;
